import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Link,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  AutoAwesome as AiIcon,
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  Lightbulb as HintIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api, { getMockExamApiPrefix, getMockExamPortalHomePath, getMockExamResultPath } from '../../../api';
import useExamMode from '../../../hooks/useExamMode';
import MarksBreakdownTable from '../components/MarksBreakdownTable';
import { stripCommonUnit } from '../../../utils/answerMatchUtils';

const COLORS = ['#0088FE', '#FF8042', '#FFBB28', '#00C49F'];

/**
 * Normalize a single value for fuzzy rule-based comparison.
 * Mirrors the backend ruleFuzzyMatch normalization:
 * time (7.46→7:46), ordinals (3rd lap→3), units (cm/kg/etc.), pence (50p→50), degree (27°C→27).
 */
const ruleNorm = (v) => {
  let s = String(v ?? '').toLowerCase().trim();
  s = s.replace(/^\(\s*(?:\\[a-z]+|[-+x×*÷/])\s*\)\s*/u, ''); // operator prefix (incl. LaTeX: \div, \times)
  s = s.replace(/÷/g, '/');
  s = s.replace(/[£$€°]/g, '');                    // currency / degree
  s = s.replace(/\s*(am|pm)\s*$/i, '');             // am/pm
  s = s.replace(/^(\d{1,2})\.(\d{2})$/, '$1:$2');  // 7.46 → 7:46
  s = s.replace(/^(\d+)(?:st|nd|rd|th)(?:\s+\w+)*$/, '$1'); // ordinal
  s = s.replace(/^(\d+(?:\.\d+)?)\s*p$/, '$1');    // pence suffix
  // One vocabulary with the take pages and the backend (AnswerUnitVocabulary), including any
  // admin-added units — this list had drifted and was missing speed and imperial units entirely.
  s = stripCommonUnit(s);
  s = s.replace(/\s*\bgrams?\s*$/i, '');
  return s.trim().replace(/\s+/g, ' ');
};

/**
 * Extract alternatives from a correct answer string (mirrors backend extractCorrectAlternatives).
 */
const extractReportAlternatives = (correct) => {
  const c = String(correct ?? '');
  if (c.includes(' (or) ')) return c.split(' (or) ').map((p) => p.trim()).filter(Boolean);
  if (/ or /i.test(c)) return c.split(/ or /i).map((p) => p.trim()).filter(Boolean);
  if (c.includes(' / ')) return c.split(' / ').map((p) => p.trim()).filter(Boolean);
  return [c];
};

/**
 * Parse a value string into [number, unit] for unit-conversion comparison.
 * Handles: "14400p" → [14400, "p"], "£144" → [14400, "p"], "95cm" → [95, "cm"]
 */
const ruleParseUnit = (v) => {
  const s = String(v ?? '').toLowerCase().trim();
  // £ prefix → convert to pence for comparison
  const poundMatch = s.match(/^£\s*(\d+(?:\.\d+)?)\s*$/);
  if (poundMatch) return [parseFloat(poundMatch[1]) * 100, 'p'];
  // number + unit suffix (longer units first to avoid partial matches)
  const unitMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*(km²|cm²|m²|km\/h|m\/s|mm|cm|km|mg|kg|ml|m|g|l|p)\s*$/);
  if (unitMatch) return [parseFloat(unitMatch[1]), unitMatch[2]];
  // bare number
  if (/^-?\d+(?:\.\d+)?\s*$/.test(s)) return [parseFloat(s), ''];
  return null;
};

// Conversion table: unit → [factor_to_canonical, canonical_unit, dimension]
const RULE_CONV = {
  mm: [1, 'mm', 'len'], cm: [10, 'mm', 'len'], m: [1000, 'mm', 'len'], km: [1000000, 'mm', 'len'],
  'cm²': [100, 'mm²', 'area'], 'm²': [1000000, 'mm²', 'area'],
  mg: [1, 'mg', 'wt'], g: [1000, 'mg', 'wt'], kg: [1000000, 'mg', 'wt'],
  ml: [1, 'ml', 'vol'], l: [1000, 'ml', 'vol'],
  p: [1, 'p', 'cur'],
};
const RULE_P2C = { p: 100, mm: 10, cm: 100, m: 1000, mg: 1000, g: 1000, ml: 1000 };

const ruleUnitEquivalent = (submitted, correct) => {
  const sp = ruleParseUnit(String(submitted ?? ''));
  const cp = ruleParseUnit(String(correct ?? ''));
  if (!sp || !cp) return false;
  const [sn, su] = sp; const [cn, cu] = cp;
  if (su && cu) {
    const sc = RULE_CONV[su]; const cc = RULE_CONV[cu];
    if (sc && cc && sc[2] === cc[2]) return Math.abs(sn * sc[0] - cn * cc[0]) < 0.001;
    return false;
  }
  if (!su && cu) {
    const f = RULE_P2C[cu]; if (f && Math.abs(sn * f - cn) < 0.001) return true;
    return Math.abs(sn - cn) < 0.001;
  }
  if (su && !cu) {
    const f = RULE_P2C[su]; if (f && Math.abs(cn * f - sn) < 0.001) return true;
    return Math.abs(sn - cn) < 0.001;
  }
  return false;
};

const ruleSingleMatch = (submitted, correct) => {
  const s = ruleNorm(String(submitted ?? ''));
  if (!s) return false;
  const alts = extractReportAlternatives(String(correct ?? ''));
  // Direct normalized match against each alternative
  if (alts.some((alt) => { const c = ruleNorm(alt); return c !== '' && s === c; })) return true;
  // Unit conversion match (e.g. "14400p" = "£144", "95cm" = "0.95m")
  return alts.some((alt) => ruleUnitEquivalent(String(submitted ?? ''), alt));
};

/**
 * Rule-based full-correctness check: returns true when the student's answer fully
 * matches the correct answer under all fuzzy-matching rules (time, ordinal, units, etc.).
 * For multi-fill arrays: every blank must match.
 * For strings: direct fuzzy match (including time/ordinal normalization).
 */
const isRuleCorrect = (submittedAnswer, correctAnswer) => {
  if (submittedAnswer == null || submittedAnswer === '' || correctAnswer == null || correctAnswer === '') return false;
  if (Array.isArray(submittedAnswer) && Array.isArray(correctAnswer)) {
    if (correctAnswer.length === 0) return false;
    return correctAnswer.every((c, i) => ruleSingleMatch(submittedAnswer[i] ?? '', c));
  }
  if (!Array.isArray(submittedAnswer) && !Array.isArray(correctAnswer)) {
    return ruleSingleMatch(submittedAnswer, correctAnswer);
  }
  return false;
};

/**
 * Returns true when the submitted value matches SOME but not ALL of the required values
 * in a comma-separated correct answer — mirrors the rule-based partial scoring logic.
 * e.g. submitted "31", correct "31, 62" → partially correct (1 of 2 values).
 */
const isRulePartial = (submittedAnswer, correctAnswer) => {
  if (submittedAnswer == null || submittedAnswer === '' || correctAnswer == null || correctAnswer === '') return false;
  const split = (v) => String(v ?? '').split(/[\n\r,;]+/).map((p) => p.trim()).filter(Boolean);
  // Never treat alternatives-format answers as partial: "1,60 (or) 2,30" is not a required list
  if (!Array.isArray(correctAnswer) && extractReportAlternatives(String(correctAnswer)).length > 1) return false;
  const submitted = Array.isArray(submittedAnswer) ? submittedAnswer.map(String) : split(String(submittedAnswer));
  const correct   = Array.isArray(correctAnswer)   ? correctAnswer.map(String)   : split(String(correctAnswer));
  if (correct.length <= 1) return false;
  if (submitted.length >= correct.length) return false;
  return submitted.some((sp) => correct.some((cp) => ruleSingleMatch(sp, cp)));
};

const PERFORMANCE_LEVEL_CONFIG = {
  excellent: { label: 'Excellent', bgcolor: '#dcfce7', color: '#166534' },
  good: { label: 'Good', bgcolor: '#dbeafe', color: '#1e40af' },
  average: { label: 'Average', bgcolor: '#fef9c3', color: '#854d0e' },
  needs_improvement: { label: 'Needs Improvement', bgcolor: '#fee2e2', color: '#991b1b' },
  unknown: { label: 'Unknown', bgcolor: '#f3f4f6', color: '#374151' },
};

// ---- AI Insights Tab Content ----
const AiInsightsTab = ({ aiFeedbackStatus, aiOverall, aiScore, aiPercentage, totalMarks, ruleScore }) => {
  if (aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: '#7c3aed' }} />
        <Typography variant="body1" sx={{ color: '#7c3aed', fontWeight: 500 }}>
          AI is analysing your answers…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This usually takes 5–15 seconds. The page will update automatically.
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === 'failed' || !aiOverall) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        AI feedback is not available for this attempt. This may be due to a temporary issue.
      </Alert>
    );
  }

  const perfConfig = PERFORMANCE_LEVEL_CONFIG[aiOverall.performance_level] ?? PERFORMANCE_LEVEL_CONFIG.unknown;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Score comparison card */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card variant="outlined" sx={{ borderColor: '#e0d7ff', bgcolor: '#f3f0ff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AiIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                AI-Adjusted Score
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#7c3aed', mb: 0.5 }}>
              {Number(aiScore ?? 0).toFixed(1)}
              <Typography component="span" variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {' '}/ {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: '#7c3aed', fontWeight: 600 }}>
              {Number(aiPercentage ?? 0).toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Based on semantic answer matching
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUpIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Performance Level
              </Typography>
            </Box>
            <Chip
              label={perfConfig.label}
              sx={{
                bgcolor: perfConfig.bgcolor,
                color: perfConfig.color,
                fontWeight: 700,
                fontSize: '1rem',
                height: 36,
                px: 1,
                mb: 1,
              }}
            />
            {ruleScore != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Auto Score: {Number(ruleScore).toFixed(1)} / {Number(totalMarks ?? 0).toFixed(1)}
                {Number(ruleScore) === 0 && (
                  <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.disabled' }}>
                    (drawings/essays scored by AI only)
                  </Typography>
                )}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Strengths & Weaknesses */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {aiOverall.strengths?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: '#bbf7d0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#166534' }}>
                  Strengths
                </Typography>
              </Box>
              {aiOverall.strengths.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a', mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {s}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {aiOverall.weaknesses?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: '#fecaca' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <WrongIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#991b1b' }}>
                  Areas to Improve
                </Typography>
              </Box>
              {aiOverall.weaknesses.map((w, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#dc2626', mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {w}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Study Recommendations */}
      {aiOverall.recommendations?.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: '#bfdbfe' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <HintIcon sx={{ color: '#2563eb', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e40af' }}>
                Study Recommendations
              </Typography>
            </Box>
            {aiOverall.recommendations.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: '#dbeafe',
                    color: '#1e40af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    mt: 0.1,
                  }}
                >
                  {i + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {r}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const StudentMockExamReport = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { isTestingMode } = useExamMode();
  const isTestMode = isTestingMode('mock_exams');
  const [tab, setTab] = useState('score');
  const [summary, setSummary] = useState(null);
  const [attemptMeta, setAttemptMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjectData, setSubjectData] = useState(null);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [topicData, setTopicData] = useState(null);
  const [topicLoading, setTopicLoading] = useState(false);
  const [error, setError] = useState(null);

  // AI feedback state
  const [aiFeedbackStatus, setAiFeedbackStatus] = useState('pending');
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [aiPercentage, setAiPercentage] = useState(null);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  // Review data for question-level breakdown (correct / partially correct / incorrect)
  const [reviewQuestions, setReviewQuestions] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!attemptId) return;
    setLoading(true);
    setError(null);
    try {
      const px = getMockExamApiPrefix();
      const [attemptRes, analyticsRes] = await Promise.all([
        api.get(`/${px}/mock-exam/attempt/${attemptId}`),
        api.get(`/${px}/mock-exam/attempt/${attemptId}/analytics`),
      ]);
      setAttemptMeta(attemptRes.data?.data ?? attemptRes.data);
      const analyticsData = analyticsRes.data?.data ?? analyticsRes.data;
      setSummary(analyticsData);
      // Seed AI state from analytics response
      if (analyticsData?.ai_feedback_status) {
        setAiFeedbackStatus(analyticsData.ai_feedback_status);
      }
      if (analyticsData?.ai_score != null) {
        setAiScore(analyticsData.ai_score);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  const fetchAiFeedback = useCallback(async () => {
    if (!attemptId) return;
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/ai-feedback`);
      const payload = res.data?.data ?? res.data;
      const status = payload?.status ?? 'pending';
      setAiFeedbackStatus(status);
      if (status === 'completed') {
        setAiOverall(payload?.overall ?? null);
        const newAiScore = payload?.ai_score ?? null;
        setAiScore(newAiScore);
        setAiPercentage(payload?.ai_percentage ?? null);
        clearInterval(pollRef.current);
        // Re-fetch analytics summary so the Score tab reflects the AI-adjusted final_score
        try {
          const analyticsRes = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/analytics`);
          const analyticsData = analyticsRes.data?.data ?? analyticsRes.data;
          setSummary(analyticsData);
        } catch {
          // Non-critical: keep existing summary
        }
        // Fetch review so we can show correct / partially correct / incorrect breakdown
        try {
          const reviewRes = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/review`);
          const reviewData = reviewRes.data?.data ?? reviewRes.data;
          setReviewQuestions(reviewData?.questions ?? null);
        } catch {
          // Non-critical
        }
      } else if (status === 'failed') {
        clearInterval(pollRef.current);
      }
    } catch {
      // Silently ignore polling errors
    }
  }, [attemptId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Start polling for AI feedback
  useEffect(() => {
    if (!attemptId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        return;
      }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (aiFeedbackStatus === 'completed' || aiFeedbackStatus === 'failed') {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  const fetchSubject = useCallback(async () => {
    if (!attemptId) return;
    setSubjectLoading(true);
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/analytics/subject`);
      setSubjectData(res.data?.data ?? res.data);
    } catch (err) {
      toast.error('Failed to load subject analytics');
      setSubjectData([]);
    } finally {
      setSubjectLoading(false);
    }
  }, [attemptId]);

  const fetchTopic = useCallback(async () => {
    if (!attemptId) return;
    setTopicLoading(true);
    try {
      const res = await api.get(`/${getMockExamApiPrefix()}/mock-exam/attempt/${attemptId}/analytics/topic`);
      setTopicData(res.data?.data ?? res.data);
    } catch (err) {
      toast.error('Failed to load topic analytics');
      setTopicData([]);
    } finally {
      setTopicLoading(false);
    }
  }, [attemptId]);

  useEffect(() => {
    if (tab === 'subject' && subjectData === null && !subjectLoading) fetchSubject();
  }, [tab, subjectData, subjectLoading, fetchSubject]);

  useEffect(() => {
    if (tab === 'topic' && topicData === null && !topicLoading) fetchTopic();
  }, [tab, topicData, topicLoading, fetchTopic]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !summary) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Card sx={{ p: 4, textAlign: 'center', maxWidth: 440 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: '#374151' }} />
          </Box>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Report could not be loaded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error || 'This report is not available. The attempt may have been removed or you may not have access.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate(getMockExamPortalHomePath())}>
            Back to Mock Exams
          </Button>
        </Card>
      </Box>
    );
  }

  // Use AI score for display if available
  const displayScore = aiScore != null ? aiScore : (summary.final_score ?? summary.scored_marks ?? 0);
  const displayPercentage = aiScore != null && summary.total_marks > 0
    ? Math.round((aiScore / summary.total_marks) * 1000) / 10
    : (summary.percentage ?? 0);

  // From review questions (when AI completed), compute correct / partially correct / incorrect counts.
  // When AI says "Incorrect" but rule-based says "Partially correct" (e.g. student typed "31"
  // for correct answer "31, 62"), trust the rule-based verdict for display.
  const aiBreakdown = (() => {
    if (aiFeedbackStatus !== 'completed' || !Array.isArray(reviewQuestions) || reviewQuestions.length === 0) {
      return null;
    }
    let correct = 0;
    let partiallyCorrect = 0;
    let incorrect = 0;
    let unattended = 0;
    reviewQuestions.forEach((q) => {
      const items = q.type === 'grouped' && Array.isArray(q.sub_questions)
        ? q.sub_questions
        : [{ ...q, ai_is_correct: q.ai_is_correct, ai_partial_score: q.ai_partial_score, submitted_answer: q.submitted_answer }];
      items.forEach((item) => {
        const ps = item.ai_partial_score;
        const aiCorrect = item.ai_is_correct;
        const hasAnswer = (item.submitted_answer != null && item.submitted_answer !== '') || !!item.submitted_drawing || (Array.isArray(item.your_answer_interactive) && item.your_answer_interactive.length > 0);
        if (!hasAnswer) {
          unattended += 1;
          return;
        }
        const ruleFullCorrect = aiCorrect !== true && isRuleCorrect(item.submitted_answer, item.correct_answer);
        if (aiCorrect === true || ruleFullCorrect) {
          correct += 1;
        } else if ((ps != null && ps > 0 && ps < 1) ||
                   // Rule-based partial override: AI says wrong but rule says partial
                   (aiCorrect !== true && isRulePartial(item.submitted_answer, item.correct_answer))) {
          partiallyCorrect += 1;
        } else {
          incorrect += 1;
        }
      });
    });
    return { correct, partiallyCorrect, incorrect, unattended };
  })();

  // When AI breakdown is available, use AI sub-question counts as the authoritative total
  const displayTotalQuestions = aiBreakdown
    ? aiBreakdown.correct + aiBreakdown.partiallyCorrect + aiBreakdown.incorrect + aiBreakdown.unattended
    : (summary.paper_total_questions ?? summary.total_questions ?? 0);

  // Recompute unattended marks from AI review data so drawing-only answers aren't counted as unattended
  const aiUnattendedMarks = (() => {
    if (aiFeedbackStatus !== 'completed' || !Array.isArray(reviewQuestions) || reviewQuestions.length === 0) return null;
    let total = 0;
    reviewQuestions.forEach((q) => {
      const items = q.type === 'grouped' && Array.isArray(q.sub_questions) ? q.sub_questions : [q];
      items.forEach((item) => {
        const hasAnswer = (item.submitted_answer != null && item.submitted_answer !== '') || !!item.submitted_drawing || (Array.isArray(item.your_answer_interactive) && item.your_answer_interactive.length > 0);
        if (!hasAnswer) total += Number(item.marks ?? 0);
      });
    });
    return total;
  })();

  const barChartData = [
    {
      name: 'Performance',
      totalMarks: summary.total_marks ?? 0,
      marksScored: displayScore,
    },
  ];

  const pieChartData = aiBreakdown
    ? [
        { name: 'Correct', value: aiBreakdown.correct },
        { name: 'Partially correct', value: aiBreakdown.partiallyCorrect },
        { name: 'Incorrect', value: aiBreakdown.incorrect },
        { name: 'Unattended', value: aiBreakdown.unattended },
      ].filter((d) => d.value > 0)
    : [
        { name: 'Correct', value: summary.correct_questions ?? 0 },
        { name: 'Incorrect', value: summary.incorrect_questions ?? 0 },
        { name: 'Unattended', value: summary.unattended_questions ?? 0 },
      ].filter((d) => d.value > 0);

  const subjectRows = Array.isArray(subjectData) ? subjectData : [];
  const topicRows = Array.isArray(topicData) ? topicData : [];

  const topicChartData = topicRows
    .filter((r) => r.name !== 'Grand Total')
    .map((r) => ({
      name: r.name,
      maxMarks: r.max_marks ?? 0,
      correctMarks: (r.correct_marks ?? 0) - (r.incorrect_marks ?? 0),
    }));

  // Flatten review questions into a list of { displayNumber, status, questionId, questionBankId, importId } for clickable breakdown.
  // questionBankId: from "bank_123" or "bank_123_sub_0" -> 123 (for link to question bank edit or import review).
  const questionBreakdownList = (() => {
    if (aiFeedbackStatus !== 'completed' || !Array.isArray(reviewQuestions) || reviewQuestions.length === 0) {
      return [];
    }
    const list = [];
    let displayNumber = 0;
    reviewQuestions.forEach((q) => {
      const items = q.type === 'grouped' && Array.isArray(q.sub_questions)
        ? q.sub_questions
        : [{ ...q, ai_is_correct: q.ai_is_correct, ai_partial_score: q.ai_partial_score, submitted_answer: q.submitted_answer }];
      items.forEach((item) => {
        displayNumber += 1;
        const hasAnswer = (item.submitted_answer != null && item.submitted_answer !== '') || !!item.submitted_drawing || (Array.isArray(item.your_answer_interactive) && item.your_answer_interactive.length > 0);
        let status = 'unattended';
          if (hasAnswer) {
          const ruleOk = item.ai_is_correct !== true && isRuleCorrect(item.submitted_answer, item.correct_answer);
          if (item.ai_is_correct === true || ruleOk) {
            status = 'correct';
          } else if (
            (item.ai_partial_score != null && item.ai_partial_score > 0 && item.ai_partial_score < 1) ||
            (item.ai_is_correct !== true && isRulePartial(item.submitted_answer, item.correct_answer))
          ) {
            status = 'partiallyCorrect';
          } else {
            status = 'incorrect';
          }
        }
        const rawId = item.id ?? q.id;
        let questionBankId = null;
        if (typeof rawId === 'string' && rawId.startsWith('bank_')) {
          const numPart = rawId.replace(/^bank_(\d+).*/, '$1');
          if (numPart && numPart !== rawId) questionBankId = parseInt(numPart, 10);
        }
        list.push({
          displayNumber,
          status,
          questionId: rawId,
          questionBankId,
          importId: summary?.question_bank_import_id ?? attemptMeta?.question_bank_import_id ?? null,
        });
      });
    });
    return list;
  })();

  const marksBreakdownRows = (() => {
    if (aiFeedbackStatus !== 'completed' || !Array.isArray(reviewQuestions) || reviewQuestions.length === 0) {
      return [];
    }
    const rows = [];
    let mainNum = 0;
    reviewQuestions.forEach((q) => {
      mainNum += 1;
      const isGrouped = q.type === 'grouped' && Array.isArray(q.sub_questions) && q.sub_questions.length > 0;
      const items = isGrouped ? q.sub_questions : [q];
      items.forEach((item, idx) => {
        const marks = Number(item.marks ?? 0);
        const hasAnswer = (item.submitted_answer != null && item.submitted_answer !== '') || !!item.submitted_drawing || (Array.isArray(item.your_answer_interactive) && item.your_answer_interactive.length > 0);
        const ps = item.ai_partial_score;
        const aiCorrect = item.ai_is_correct;
        let result = 'unanswered';
        let earned = 0;
        if (hasAnswer) {
          const ruleFullCorrect = aiCorrect !== true && isRuleCorrect(item.submitted_answer, item.correct_answer);
          if (aiCorrect === true || ruleFullCorrect) {
            result = 'correct';
            earned = marks;
          } else if ((ps != null && ps > 0 && ps < 1) || (aiCorrect !== true && isRulePartial(item.submitted_answer, item.correct_answer))) {
            result = 'partial';
            earned = ps != null && ps > 0 ? Math.round(ps * marks * 100) / 100 : Math.round(marks * 0.5 * 100) / 100;
          } else {
            result = 'incorrect';
          }
        }
        rows.push({
          key: `${item.id ?? q.id}-${idx}`,
          qLabel: idx === 0 ? `Q${mainNum}` : '',
          partLabel: isGrouped ? String.fromCharCode(97 + idx).toUpperCase() : '',
          questionText: item.question_text,
          result,
          earned,
          max: marks,
          isMainRow: idx === 0,
        });
      });
    });
    return rows;
  })();

  const getQuestionLink = (item) => {
    // Strip sub-question suffix (bank_123_sub_0 → bank_123) to scroll to the parent card
    const parentId = typeof item.questionId === 'string'
      ? item.questionId.replace(/_sub_\d+$/, '')
      : String(item.questionId ?? '');
    if (parentId) {
      return `${getMockExamResultPath(attemptId, 'review')}#question-${parentId}`;
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" sx={{ mb: 2 }} onClick={() => navigate(getMockExamPortalHomePath())}>
        Back to Mock Exams
      </Button>
      {isTestMode && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — This is a practice attempt. Results are not recorded.
        </Alert>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {summary.exam_title || attemptMeta?.exam_name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            Analytics Report for {summary.student_name || attemptMeta?.student_name || 'Student'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(aiScore).toFixed(1)} / ${Number(summary.total_marks ?? 0).toFixed(1)}`}
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(displayPercentage).toFixed(1)}%`} variant="outlined" color="secondary" />
                <Chip
                  label={`Auto Score: ${Number(aiScore).toFixed(1)} / ${Number(summary.total_marks ?? 0).toFixed(1)}`}
                  variant="outlined"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                />
              </>
            ) : (
              <>
                <Chip
                  label={`Score: ${Number(summary.final_score ?? summary.scored_marks ?? 0).toFixed(1)} / ${Number(summary.total_marks ?? 0).toFixed(1)}`}
                  color="primary"
                />
                <Chip label={`${Number(summary.percentage ?? 0).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {(aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') && (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: '#7c3aed' }} />}
                label="AI feedback generating…"
                size="small"
                sx={{ bgcolor: '#f3f0ff', color: '#7c3aed', border: '1px solid #e0d7ff' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab value="score" label="Score" />
        <Tab value="subject" label="Subject" />
        <Tab value="topic" label="Topic" />
        <Tab value="marks" label="Marks Breakdown" />
        <Tab
          value="ai"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AiIcon sx={{ fontSize: 16 }} />
              AI Insights
              {(aiFeedbackStatus === 'pending' || aiFeedbackStatus === 'processing') && (
                <CircularProgress size={10} sx={{ ml: 0.5, color: '#7c3aed' }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {tab === 'score' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Questions
                </Typography>
                <Typography variant="body2">Total: {displayTotalQuestions}</Typography>
                {aiBreakdown ? (
                  <>
                    <Typography variant="body2" color="success.main">
                      Correct: {aiBreakdown.correct}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b45309' }}>
                      Partially correct: {aiBreakdown.partiallyCorrect}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      Incorrect: {aiBreakdown.incorrect}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unattended: {aiBreakdown.unattended}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="success.main">
                      Correct: {summary.correct_questions}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      Incorrect: {summary.incorrect_questions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unattended: {summary.unattended_questions}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Marks
                </Typography>
                <Typography variant="body2">
                  Marking: +{(summary.marks_per_correct_answer ?? 1).toFixed(2)} / -
                  {(summary.negative_marks_penalty ?? 0).toFixed(2)}
                </Typography>
                <Typography variant="body2">Total: {summary.total_marks}</Typography>
                <Typography variant="body2" color="success.main">
                  Scored: {Number(displayScore).toFixed(1)}
                  {aiScore != null && (
                    <Typography component="span" variant="caption" sx={{ color: '#7c3aed', ml: 0.5 }}>
                      (AI)
                    </Typography>
                  )}
                </Typography>
                {aiScore != null && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Partial credit applied for sub-questions, multi-fill blanks, and table cells.
                  </Typography>
                )}
                <Typography variant="body2" color="error.main">
                  Negative: {summary.negative_marks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unattended: {aiUnattendedMarks != null ? Number(aiUnattendedMarks).toFixed(1) : summary.unattended_marks}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Summary
                </Typography>
                <Typography variant="body2">Time Taken: {summary.time_taken ?? 'N/A'}</Typography>
                <Typography variant="body2">
                  Final Score: {Number(displayScore).toFixed(1)}/{summary.total_marks}
                  {aiScore != null && (
                    <Typography component="span" variant="caption" sx={{ color: '#7c3aed', ml: 0.5 }}>
                      (AI-adjusted)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Percentage: {Number(displayPercentage).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Performance Overview
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={barChartData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} />
                    <YAxis tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="totalMarks" name="Total Marks" fill="#8884d8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="marksScored" name={aiScore != null ? 'AI Score' : 'Marks Scored'} fill={aiScore != null ? '#7c3aed' : '#82ca9d'} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Question Analysis
                </Typography>
                {pieChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary">No question breakdown (all unattended).</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
          {questionBreakdownList.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Question breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Click a question to open it in Question Bank (edit) or Import Review when available.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {questionBreakdownList.map((item) => {
                        const link = getQuestionLink(item);
                        const statusLabel =
                          item.status === 'correct'
                            ? 'Correct'
                            : item.status === 'partiallyCorrect'
                              ? 'Partially correct'
                              : item.status === 'incorrect'
                                ? 'Incorrect'
                                : 'Unattended';
                        const statusColor =
                          item.status === 'correct'
                            ? 'success.main'
                            : item.status === 'partiallyCorrect'
                              ? '#b45309'
                              : item.status === 'incorrect'
                                ? 'error.main'
                                : 'text.secondary';
                        return (
                          <TableRow
                            key={`q-${item.displayNumber}-${item.questionId}`}
                            hover={!!link}
                            onClick={link ? () => navigate(link) : undefined}
                            sx={{
                              cursor: link ? 'pointer' : 'default',
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>Q{item.displayNumber}</TableCell>
                            <TableCell sx={{ color: statusColor, fontWeight: 500 }}>{statusLabel}</TableCell>
                            <TableCell>
                              {link ? (
                                <Link
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    textDecoration: 'underline',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  View Question
                                </Link>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {tab === 'subject' && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Subject-wise Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Detailed performance breakdown by subject.
            </Typography>
            {subjectLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Total Qs</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Correct</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Incorrect</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unattempted</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Correct Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Incorrect Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unattempted Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Max Marks</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjectRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: row.name === 'Grand Total' ? 700 : 400 }}>
                          {row.name}
                        </TableCell>
                        <TableCell>{row.paper_total_questions ?? row.total_questions}</TableCell>
                        <TableCell sx={{ color: 'success.main' }}>{row.correct}</TableCell>
                        <TableCell sx={{ color: 'error.main' }}>{row.incorrect}</TableCell>
                        <TableCell>{row.unattempted}</TableCell>
                        <TableCell>{Number(row.correct_marks).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.incorrect_marks).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.unattempted_marks).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.max_marks).toFixed(2)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{row.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'topic' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Topic-wise Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Detailed performance breakdown by topic.
              </Typography>
              {topicLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Total Qs</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Correct</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Incorrect</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unattempted</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Correct Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Incorrect Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unattempted Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Max Marks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topicRows.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: row.name === 'Grand Total' ? 700 : 400 }}>
                            {row.name}
                          </TableCell>
                          <TableCell>{row.paper_total_questions ?? row.total_questions}</TableCell>
                          <TableCell sx={{ color: 'success.main' }}>{row.correct}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{row.incorrect}</TableCell>
                          <TableCell>{row.unattempted}</TableCell>
                          <TableCell>{Number(row.correct_marks).toFixed(2)}</TableCell>
                          <TableCell>{Number(row.incorrect_marks).toFixed(2)}</TableCell>
                          <TableCell>{Number(row.unattempted_marks).toFixed(2)}</TableCell>
                          <TableCell>{Number(row.max_marks).toFixed(2)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{row.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          {topicChartData.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Topic Performance
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topicChartData}
                    layout="vertical"
                    margin={{ left: 80, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={80} tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="maxMarks" name="Max Marks" fill="#8884d8" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="correctMarks" name="Correct Marks" fill="#82ca9d" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {tab === 'marks' && (
        marksBreakdownRows.length > 0 ? (
          <MarksBreakdownTable
            rows={marksBreakdownRows}
            totalMarks={summary.total_marks ?? 0}
            scoredMarks={summary.final_score ?? displayScore}
            title="Marks Breakdown"
            fileName={`${(summary.exam_title || 'mock-exam').replace(/[^\w-]+/g, '_').slice(0, 60)}-marks`}
          />
        ) : (
          <Alert severity="info">Marks breakdown will be available once AI feedback is complete.</Alert>
        )
      )}

      {tab === 'ai' && (
        <AiInsightsTab
          aiFeedbackStatus={aiFeedbackStatus}
          aiOverall={aiOverall}
          aiScore={aiScore}
          aiPercentage={aiPercentage}
          totalMarks={summary.total_marks}
          ruleScore={summary.scored_marks}
        />
      )}
    </Box>
  );
};

export default StudentMockExamReport;
